import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAreSectionsPending } from "@/contexts/company-sections-context";
import { cn } from "@/lib/utils";
import type { CompanySectionKey } from "@shaarp/common";
import type { ReactNode } from "react";

type CompanySectionCardProps = {
  /** The sections the card is filled from. */
  readonly sections: readonly CompanySectionKey[];
  /** Whether the company already holds what the card shows. */
  readonly hasData: boolean;
  /** Shown while the card has nothing yet and one of its sections runs. */
  readonly skeleton?: ReactNode;
  readonly children: ReactNode;
};

/**
 * One card of the company page, gated on its own sections rather than on the
 * whole page: the card as soon as the company holds its data, its skeleton
 * while it has none and one of its sections is still pending, nothing once
 * they all settled without data — empty, failed, or cut by a spent budget,
 * which the page's banner explains.
 */
export default function CompanySectionCard({
  sections,
  hasData,
  skeleton = null,
  children,
}: CompanySectionCardProps) {
  const isPending = useAreSectionsPending(sections);

  if (hasData) return <>{children}</>;
  if (isPending) return <>{skeleton}</>;
  return null;
}

/** The placeholder of a card that has no skeleton of its own: a title, two lines. */
export function CompanyCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn("flex flex-col min-w-0 overflow-hidden", className)}>
      <CardHeader className="flex flex-row items-center gap-3">
        <Skeleton variant="subtle" className="w-10 h-10 rounded-full shrink-0" />
        <Skeleton variant="subtle" className="h-3 w-1/3" />
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <Skeleton variant="subtle" className="h-2 w-2/3" />
        <Skeleton variant="subtle" className="h-2 w-1/2" />
      </CardContent>
    </Card>
  );
}
